export const THINKING_VERBS = [
  'Computing',
  'Reasoning',
  'Reflecting',
  'Considering',
  'Deducing',
  'Synthesizing',
  'Untangling',
  'Wrangling',
  'Finagling',
  'Hatching',
  'Conjuring',
  'Divining',
  'Manifesting',
  'Forging',
  'Crafting',
  'Spelunking',
  'Wandering',
  'Marinating',
  'Stewing',
  'Schlepping',
  'Vibing',
  'Puzzling',
] as const;

const ROTATION_MS = 1500;

// Same elapsed-time bucket → same verb across UI surfaces (terminal + bubble).
// Seeded by startedAt so two simultaneous agents pick different verbs.
export function pickVerb(startedAt: number, now: number = Date.now()): string {
  const elapsed = Math.max(0, now - startedAt);
  const slot = Math.floor(elapsed / ROTATION_MS);
  const seed = Math.floor(startedAt / 1000) % THINKING_VERBS.length;
  return THINKING_VERBS[(seed + slot) % THINKING_VERBS.length];
}

export function elapsedSeconds(startedAt: number, now: number = Date.now()): number {
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

// Cheap token estimate: ~4 chars per token. Good enough for a status bar.
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

export function formatTokenCount(tokens: number): string {
  if (tokens < 1000) return `${tokens}`;
  return `${(tokens / 1000).toFixed(1)}k`;
}

/** Truncate a (non-ANSI) string so a write at column 0 cannot wrap the line. */
export function clampToCols(plain: string, cols: number): string {
  const max = Math.max(0, cols - 1);
  return plain.length <= max ? plain : plain.slice(0, max);
}

/** Last path segment of a path-ish string (handles both / and \ separators). */
function baseName(p: string): string {
  const parts = p.split(/[\\/]/);
  return parts[parts.length - 1] || p;
}

/** Humanize a tool name into a Title-cased phrase: strips any mcp__server__ prefix. */
function humanizeToolName(name: string): string {
  const last = name.split('__').pop() ?? name;
  const spaced = last.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : 'Working';
}

/**
 * Explicit, human phrase describing what an agent is doing right now, derived
 * from a tool_use (name + input). Drives the single dynamic status line —
 * "Reading beachhead.md", "Running npm", "Searching for …". Falls back to a
 * humanized tool name so unknown/MCP tools still read cleanly.
 */
export function activityPhrase(toolName: string, input?: unknown): string {
  const io = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  const str = (k: string): string | undefined => (typeof io[k] === 'string' ? (io[k] as string) : undefined);
  switch (toolName) {
    case 'Read':
    case 'NotebookRead': {
      const f = str('file_path') ?? str('path');
      return f ? `Reading ${baseName(f)}` : 'Reading files';
    }
    case 'Write': {
      const f = str('file_path') ?? str('path');
      return f ? `Writing ${baseName(f)}` : 'Writing a file';
    }
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit': {
      const f = str('file_path') ?? str('path');
      return f ? `Editing ${baseName(f)}` : 'Editing a file';
    }
    case 'Grep': {
      const p = str('pattern');
      return p ? `Searching for “${p.slice(0, 32)}”` : 'Searching files';
    }
    case 'Glob':
      return 'Finding files';
    case 'LS':
      return 'Listing files';
    case 'Bash': {
      const first = str('command')?.trim().split(/\s+/)[0];
      return first ? `Running ${first}` : 'Running command';
    }
    default:
      return humanizeToolName(toolName);
  }
}

/**
 * Extract the last meaningful phrase from a thinking stream for the spinner peek.
 * Returns the last sentence fragment (after the last ". " / "? " / "! "), trimmed
 * to maxLen characters at a word boundary. Returns '' when the buffer is empty.
 */
export function lastSentenceFragment(text: string, maxLen: number): string {
  if (!text.trim()) return '';
  const normalized = text.replace(/\s+/g, ' ').trim();
  const last = Math.max(
    normalized.lastIndexOf('. '),
    normalized.lastIndexOf('? '),
    normalized.lastIndexOf('! '),
  );
  const fragment = last >= 0 ? normalized.slice(last + 2).trim() : normalized;
  if (fragment.length <= maxLen) return fragment;
  const truncated = fragment.slice(0, maxLen);
  const sp = truncated.lastIndexOf(' ');
  return sp > maxLen * 0.5 ? truncated.slice(0, sp) + '…' : truncated + '…';
}
